import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { equals } from '@fcom/core/utils/equals';
import { resolveTailName } from '@fcom/core/utils/utils';
import { Airline } from '@fcom/common/interfaces/airlines';
import { getCurrentBreakPoint } from '@fcom/common/utils/layout.utils';
import { FinnairOperatingAirlineId } from '@fcom/dapi/api/models';
import { BUS_CONNECTION_LOCATION_CODES, RAIL_AND_FLY_LOCATION_CODES } from '@fcom/core/constants';
import { LocationUtil } from '@fcom/dapi';

import { ResponsiveImage } from '../../images/interfaces';
import { AspectRatios } from '../../images';
import { JourneyType } from '../interface';
import { IconPosition } from '../../icons';
import { ButtonTheme } from '../../buttons';

@Component({
  selector: 'fcom-trip-card',
  templateUrl: './trip-card.component.html',
  styleUrls: ['./trip-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripCardComponent {
  readonly JourneyType = JourneyType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;

  /**
   * A string representing the title of the trip card
   */
  title = input<string>();

  /**
   * An object containing the image of the next destination and the alt text to use for
   * accessibility.
   *
   * If an image isn't provided then a background of grey-200 will be used
   */
  image = input<{ alt: string } & ResponsiveImage>();

  /**
   * An array of cities to display in the trip card. You should pass every city at the start and
   * end of a bound. For example Stockholm to Hong Kong should show only these cities are not
   * include Helsinki.
   *
   * Note -There is special logic using this input
   * 1. The journey type (one way, return, complex) is calculated from this input.
   * 2. For return journeys the final destination is not shown.
   */
  destinations = input<LocationUtil[]>([]);

  /**
   * The departure date of the trip.
   * @type {string}
   */
  departureDate = input<string>();

  /**
   * The arrival date of the trip.
   * @type {string}
   */
  arrivalDate = input<string>();

  /**
   * The number of passengers (including infants and children).
   */
  passengerAmount = input<number>();

  /**
   * The total price amount for the flight.
   * @type {string}
   */
  flightTotalPriceAmount = input<string>();

  /**
   * Total points for the flight
   * @type {string}
   */
  totalPoints = input<string>();

  /**
   * A flag indicating whether the expanded area is opened or closed.
   * @type {boolean}
   */
  expanded = model(false);

  /**
   * A flag indicating whether the start over button should be shown.
   * @type {boolean}
   */
  showStartOverButton = input(false);

  /**
   * A flag indicating whether the expand button should be shown.
   * @type {boolean}
   */
  showExpandButton = input(true);

  operatingAirlineIds = input<FinnairOperatingAirlineId[]>([]);

  bookingRef = input<string>();

  /**
   * Emits when the CTA element has been clicked
   * @type {OutputRef}
   */
  ctaClick = output<void>();

  notifications = viewChild<ElementRef>('notifications');

  expandContainerId = uuid();
  expandButtonId = uuid();
  imageRatio = signal<keyof typeof AspectRatios>('16x9');

  bookingReferences = computed<
    Array<{ airlineCode: string; id: string; airlineName?: string; tailName: string | undefined }>
  >(() => {
    const references: Array<{ airlineCode: string; id: string; airlineName?: string }> = [];
    const seenReferences = new Set<string>();

    const addReference = (airlineCode: string, id: string, airlineName?: string): void => {
      const key = `${airlineCode}-${id}`;
      if (id && !seenReferences.has(key)) {
        seenReferences.add(key);
        references.push({ airlineCode, id, airlineName });
      }
    };

    const finnairBookingReference = this.bookingRef();
    if (finnairBookingReference) {
      addReference(Airline.FINNAIR, finnairBookingReference);
    }

    for (const airline of this.operatingAirlineIds() ?? []) {
      addReference(airline.airlineCode, airline.id, airline.airlineName);
    }

    return references.map((reference) => ({ ...reference, tailName: resolveTailName(reference.airlineCode) }));
  });

  // Filter out matching return flight pairs, e.g.
  // [['Helsinki', 'London'], ['London', 'Helsinki']] => [['Helsinki', 'London']]
  destinationPairs = computed<string[][]>(() => {
    const dests = this.destinations();
    const pairs: string[][] = [];

    for (let i = 0; i < dests.length - 1; i += 2) {
      const pair = [dests[i]?.name, dests[i + 1]?.name];
      if (!pairs.some((p) => equals(p, [pair[1], pair[0]]))) {
        pairs.push(pair);
      }
    }
    return pairs;
  });

  journeyType = computed<JourneyType>(() => {
    const dests = this.destinations();
    const pairs = this.destinationPairs();

    if (pairs.length > 1) {
      return JourneyType.COMPLEX;
    } else if (dests.length > 2 && dests[0].name === dests[dests.length - 1].name) {
      return JourneyType.RETURN;
    } else {
      return JourneyType.ONEWAY;
    }
  });

  journeyIcon = computed<SvgLibraryIcon>(() => {
    const containsBusOrTrainDestination = this.destinations().some((destination) => {
      return (
        BUS_CONNECTION_LOCATION_CODES.includes(destination.code) ||
        RAIL_AND_FLY_LOCATION_CODES.includes(destination.code)
      );
    });

    if (containsBusOrTrainDestination) {
      return SvgLibraryIcon.MINUS;
    }

    if (this.journeyType() === JourneyType.RETURN) {
      return SvgLibraryIcon.RETURN_TRIP;
    }

    return SvgLibraryIcon.AIRCRAFT_HORIZONTAL;
  });

  @HostListener('window:resize')
  onResize(): void {
    this.updateAspectRatio();
  }

  private updateAspectRatio(): void {
    const notif = this.notifications();
    const checkAspectForMobile = getCurrentBreakPoint() === 'mobile' && notif;

    if (checkAspectForMobile && notif.nativeElement.clientHeight / 9 > notif.nativeElement.clientWidth / 16) {
      this.imageRatio.set('1x1');
    } else {
      this.imageRatio.set('16x9');
    }
  }

  toggleExpand(event: MouseEvent): void {
    // The show details link is inside another event handler and triggers twice. Stop this event
    // from propagating.
    event.stopPropagation();
    this.expanded.update((v) => !v);
  }

  clickCta(event: Event): void {
    this.ctaClick.emit();
    event.preventDefault();
  }
}
